import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "./Button";
import { useTranslation } from "react-i18next";

// Accordion Component
const AccordionItem = ({ title, children, isOpen, onToggle }) => {
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <motion.div
          animate={{ rotate: isOpen ? 90 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronRight className="h-5 w-5 text-gray-500" />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Professional Terms Accordion Component
const ProfessionalTermsAccordion = () => {
  const { t } = useTranslation();
  const [openSections, setOpenSections] = useState({
    services: false,
    responsibilities: false,
    quality: false,
    payment: false,
    conduct: false,
    maintenance: false,
    termination: false
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="space-y-0">
      <AccordionItem
        title={t("policyModals.professionalTerms.services.title")}
        isOpen={openSections.services}
        onToggle={() => toggleSection("services")}
      >
        <div className="prose prose-sm max-w-none">
          <p>{t("policyModals.professionalTerms.services.content")}</p>
        </div>
      </AccordionItem>

      <AccordionItem
        title={t("policyModals.professionalTerms.responsibilities.title")}
        isOpen={openSections.responsibilities}
        onToggle={() => toggleSection("responsibilities")}
      >
        <div className="prose prose-sm max-w-none">
          <p>{t("policyModals.professionalTerms.responsibilities.intro")}</p>
          <ul>
            <li>{t("policyModals.professionalTerms.responsibilities.items.deliverServices")}</li>
            <li>{t("policyModals.professionalTerms.responsibilities.items.meetDeadlines")}</li>
            <li>{t("policyModals.professionalTerms.responsibilities.items.maintainCommunication")}</li>
            <li>{t("policyModals.professionalTerms.responsibilities.items.accurateQualifications")}</li>
            <li>{t("policyModals.professionalTerms.responsibilities.items.respectConfidentiality")}</li>
            <li>{t("policyModals.professionalTerms.responsibilities.items.improveSkills")}</li>
          </ul>
        </div>
      </AccordionItem>

      <AccordionItem
        title={t("policyModals.professionalTerms.qualityStandards.title")}
        isOpen={openSections.quality}
        onToggle={() => toggleSection("quality")}
      >
        <div className="prose prose-sm max-w-none">
          <p>{t("policyModals.professionalTerms.qualityStandards.intro")}</p>
          <ul>
            <li>{t("policyModals.professionalTerms.qualityStandards.items.completeProjects")}</li>
            <li>{t("policyModals.professionalTerms.qualityStandards.items.respondCommunications")}</li>
            <li>{t("policyModals.professionalTerms.qualityStandards.items.provideUpdates")}</li>
            <li>{t("policyModals.professionalTerms.qualityStandards.items.resolveIssues")}</li>
          </ul>
        </div>
      </AccordionItem>

      <AccordionItem
        title={t("policyModals.professionalTerms.paymentAndFees.title")}
        isOpen={openSections.payment}
        onToggle={() => toggleSection("payment")}
      >
        <div className="prose prose-sm max-w-none">
          <p>{t("policyModals.professionalTerms.paymentAndFees.intro")}</p>
          <ul>
            <li>{t("policyModals.professionalTerms.paymentAndFees.items.platformFees")}</li>
            <li>{t("policyModals.professionalTerms.paymentAndFees.items.paymentsProcessed")}</li>
            <li>{t("policyModals.professionalTerms.paymentAndFees.items.disputesResolved")}</li>
            <li>{t("policyModals.professionalTerms.paymentAndFees.items.allTransactions")}</li>
          </ul>
        </div>
      </AccordionItem>

      <AccordionItem
        title={t("policyModals.professionalTerms.conduct.title")}
        isOpen={openSections.conduct}
        onToggle={() => toggleSection("conduct")}
      >
        <div className="prose prose-sm max-w-none">
          <p>{t("policyModals.professionalTerms.conduct.intro")}</p>
          <ul>
            <li>{t("policyModals.professionalTerms.conduct.items.maintainEthics")}</li>
            <li>{t("policyModals.professionalTerms.conduct.items.noFraud")}</li>
            <li>{t("policyModals.professionalTerms.conduct.items.respectIP")}</li>
            <li>{t("policyModals.professionalTerms.conduct.items.noSolicitation")}</li>
            <li>{t("policyModals.professionalTerms.conduct.items.reportSuspicious")}</li>
          </ul>
        </div>
      </AccordionItem>

      <AccordionItem
        title={t("policyModals.professionalTerms.accountMaintenance.title")}
        isOpen={openSections.maintenance}
        onToggle={() => toggleSection("maintenance")}
      >
        <div className="prose prose-sm max-w-none">
          <p>{t("policyModals.professionalTerms.accountMaintenance.content")}</p>
        </div>
      </AccordionItem>

      <AccordionItem
        title={t("policyModals.professionalTerms.termination.title")}
        isOpen={openSections.termination}
        onToggle={() => toggleSection("termination")}
      >
        <div className="prose prose-sm max-w-none">
          <p>{t("policyModals.professionalTerms.termination.content")}</p>
        </div>
      </AccordionItem>

      <div className="p-4 bg-gray-50 rounded-lg mt-4">
        <p className="text-sm text-gray-600">{t("policyModals.professionalTerms.lastUpdated", { date: new Date().toLocaleDateString() })}</p>
      </div>
    </div>
  );
};

/**
 * PolicyModals Component
 * Displays privacy policy and terms of service modals
 */
const PolicyModals = ({ 
  showPrivacyModal, 
  showTermsModal, 
  showProfessionalTermsModal,
  onClosePrivacy, 
  onCloseTerms,
  onCloseProfessionalTerms
}) => {
  const { t } = useTranslation();
  const currentDate = new Date().toLocaleDateString();

  return (
    <>
      {/* Privacy Policy Modal */}
      <AnimatePresence>
        {showPrivacyModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white rounded-xl max-w-3xl w-full p-6 relative max-h-[90vh] overflow-y-auto"
            >
              <button
                onClick={onClosePrivacy}
                className="absolute right-4 top-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
              
              <h2 className="text-2xl font-bold mb-6 pr-10">{t("policyModals.privacyPolicy.title")}</h2>
              
              <div className="prose prose-sm max-w-none">
                <h3>{t("policyModals.privacyPolicy.introduction.title")}</h3>
                <p>{t("policyModals.privacyPolicy.introduction.content")}</p>
                
                <h3>{t("policyModals.privacyPolicy.informationWeCollect.title")}</h3>
                <p>{t("policyModals.privacyPolicy.informationWeCollect.intro")}</p>
                <p>{t("policyModals.privacyPolicy.informationWeCollect.mayInclude")}</p>
                <ul>
                  <li>{t("policyModals.privacyPolicy.informationWeCollect.items.nameEmailPhone")}</li>
                  <li>{t("policyModals.privacyPolicy.informationWeCollect.items.profileInfo")}</li>
                  <li>{t("policyModals.privacyPolicy.informationWeCollect.items.paymentInfo")}</li>
                  <li>{t("policyModals.privacyPolicy.informationWeCollect.items.verificationDocs")}</li>
                  <li>{t("policyModals.privacyPolicy.informationWeCollect.items.communications")}</li>
                </ul>
                
                <h3>{t("policyModals.privacyPolicy.howWeUseInfo.title")}</h3>
                <p>{t("policyModals.privacyPolicy.howWeUseInfo.intro")}</p>
                <ul>
                  <li>{t("policyModals.privacyPolicy.howWeUseInfo.items.providePlatform")}</li>
                  <li>{t("policyModals.privacyPolicy.howWeUseInfo.items.connectUsers")}</li>
                  <li>{t("policyModals.privacyPolicy.howWeUseInfo.items.processTransactions")}</li>
                  <li>{t("policyModals.privacyPolicy.howWeUseInfo.items.sendMessages")}</li>
                  <li>{t("policyModals.privacyPolicy.howWeUseInfo.items.respondQuestions")}</li>
                  <li>{t("policyModals.privacyPolicy.howWeUseInfo.items.provideSupport")}</li>
                  <li>{t("policyModals.privacyPolicy.howWeUseInfo.items.monitorTrends")}</li>
                  <li>{t("policyModals.privacyPolicy.howWeUseInfo.items.personalize")}</li>
                  <li>{t("policyModals.privacyPolicy.howWeUseInfo.items.detectFraud")}</li>
                </ul>
                
                <h3>{t("policyModals.privacyPolicy.sharingInfo.title")}</h3>
                <p>{t("policyModals.privacyPolicy.sharingInfo.intro")}</p>
                <ul>
                  <li>{t("policyModals.privacyPolicy.sharingInfo.items.withUsers")}</li>
                  <li>{t("policyModals.privacyPolicy.sharingInfo.items.withProviders")}</li>
                  <li>{t("policyModals.privacyPolicy.sharingInfo.items.legalProcess")}</li>
                  <li>{t("policyModals.privacyPolicy.sharingInfo.items.corporateChange")}</li>
                </ul>
                
                <h3>{t("policyModals.privacyPolicy.security.title")}</h3>
                <p>{t("policyModals.privacyPolicy.security.content")}</p>
                
                <h3>{t("policyModals.privacyPolicy.yourRights.title")}</h3>
                <p>{t("policyModals.privacyPolicy.yourRights.content")}</p>
                
                <h3>{t("policyModals.privacyPolicy.changes.title")}</h3>
                <p>{t("policyModals.privacyPolicy.changes.content")}</p>
                
                <h3>{t("policyModals.privacyPolicy.contactUs.title")}</h3>
                <p>{t("policyModals.privacyPolicy.contactUs.content")}</p>
                
                <p className="text-sm text-gray-600 mt-6">{t("policyModals.privacyPolicy.lastUpdated", { date: currentDate })}</p>
              </div>
              
              <div className="mt-6 flex justify-end">
                <Button onClick={onClosePrivacy}>
                  {t("policyModals.privacyPolicy.iUnderstand")}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Terms of Service Modal */}
      <AnimatePresence>
        {showTermsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white rounded-xl max-w-3xl w-full p-6 relative max-h-[90vh] overflow-y-auto"
            >
              <button
                onClick={onCloseTerms}
                className="absolute right-4 top-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
              
              <h2 className="text-2xl font-bold mb-6 pr-10">{t("policyModals.termsOfService.title")}</h2>
              
              <div className="prose prose-sm max-w-none">
                <h3>{t("policyModals.termsOfService.acceptance.title")}</h3>
                <p>{t("policyModals.termsOfService.acceptance.content")}</p>
                
                <h3>{t("policyModals.termsOfService.userAccounts.title")}</h3>
                <p>{t("policyModals.termsOfService.userAccounts.content")}</p>
                
                <h3>{t("policyModals.termsOfService.platformUsage.title")}</h3>
                <h4>{t("policyModals.termsOfService.platformUsage.forClients")}</h4>
                <ul>
                  <li>{t("policyModals.termsOfService.platformUsage.clientItems.postJobs")}</li>
                  <li>{t("policyModals.termsOfService.platformUsage.clientItems.payPromptly")}</li>
                  <li>{t("policyModals.termsOfService.platformUsage.clientItems.provideClear")}</li>
                </ul>
                
                <h4>{t("policyModals.termsOfService.platformUsage.forProfessionals")}</h4>
                <ul>
                  <li>{t("policyModals.termsOfService.platformUsage.professionalItems.offerServices")}</li>
                  <li>{t("policyModals.termsOfService.platformUsage.professionalItems.accurateRepresent")}</li>
                  <li>{t("policyModals.termsOfService.platformUsage.professionalItems.deliverServices")}</li>
                </ul>
                
                <h3>{t("policyModals.termsOfService.paymentsAndFees.title")}</h3>
                <p>{t("policyModals.termsOfService.paymentsAndFees.content")}</p>
                
                <h3>{t("policyModals.termsOfService.prohibitedActivities.title")}</h3>
                <p>{t("policyModals.termsOfService.prohibitedActivities.intro")}</p>
                <ul>
                  <li>{t("policyModals.termsOfService.prohibitedActivities.items.circumvent")}</li>
                  <li>{t("policyModals.termsOfService.prohibitedActivities.items.postFalse")}</li>
                  <li>{t("policyModals.termsOfService.prohibitedActivities.items.harass")}</li>
                  <li>{t("policyModals.termsOfService.prohibitedActivities.items.illegalUse")}</li>
                  <li>{t("policyModals.termsOfService.prohibitedActivities.items.infringement")}</li>
                  <li>{t("policyModals.termsOfService.prohibitedActivities.items.unauthorizedAccess")}</li>
                </ul>
                
                <h3>{t("policyModals.termsOfService.contentOwnership.title")}</h3>
                <p>{t("policyModals.termsOfService.contentOwnership.content")}</p>
                
                <h3>{t("policyModals.termsOfService.disputeResolution.title")}</h3>
                <p>{t("policyModals.termsOfService.disputeResolution.content")}</p>
                
                <h3>{t("policyModals.termsOfService.termination.title")}</h3>
                <p>{t("policyModals.termsOfService.termination.content")}</p>
                
                <h3>{t("policyModals.termsOfService.limitationOfLiability.title")}</h3>
                <p>{t("policyModals.termsOfService.limitationOfLiability.content")}</p>
                
                <h3>{t("policyModals.termsOfService.changesToTerms.title")}</h3>
                <p>{t("policyModals.termsOfService.changesToTerms.content")}</p>
                
                <h3>{t("policyModals.termsOfService.governingLaw.title")}</h3>
                <p>{t("policyModals.termsOfService.governingLaw.content")}</p>
                
                <p className="text-sm text-gray-600 mt-6">{t("policyModals.termsOfService.lastUpdated", { date: currentDate })}</p>
              </div>
              
              <div className="mt-6 flex justify-end">
                <Button onClick={onCloseTerms}>
                  {t("policyModals.termsOfService.iAccept")}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Professional Terms Modal */}
      <AnimatePresence>
        {showProfessionalTermsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white rounded-xl max-w-4xl w-full p-6 relative max-h-[90vh] overflow-y-auto"
            >
              <button
                onClick={onCloseProfessionalTerms}
                className="absolute right-4 top-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
              
              <h2 className="text-2xl font-bold mb-6 pr-10">{t("policyModals.professionalTerms.title")}</h2>
              
              <ProfessionalTermsAccordion />
              
              <div className="mt-6 flex justify-end">
                <Button onClick={onCloseProfessionalTerms}>
                  {t("policyModals.professionalTerms.iUnderstand")}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PolicyModals;
